import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signupWithLambda, SignupRequest } from '../repository/authRepository';
import { setupAutoRefresh } from '../utils/tokenRefresh';

const Signup: React.FC = () => {
  const [formData, setFormData] = useState<SignupRequest>({
    username: '',
    email: '',
    password1: '',
    password2: '',
    terms_of_service: false,
    privacy_policy: false,
    cookie_policy: false,
  });
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (field: keyof SignupRequest, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGeneralError(null);
    setIsLoading(true);

    const response = await signupWithLambda(formData);

    if ('error' in response) {
      setGeneralError(response.error);
    } else {
      if ('access' in response && 'refresh' in response && response.user) {
        localStorage.setItem('access_token', response.access || '');
        localStorage.setItem('refresh_token', response.refresh || '');
        localStorage.setItem('user', JSON.stringify(response.user));
        setupAutoRefresh();
        navigate('/');
      } else {
        navigate('/login');
      }
    }

    setIsLoading(false);
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h2>Create your account</h2>
        <p className="app-subtitle">Track every game and keep your vocabulary wins.</p>
        {generalError && <div className="notice error">{generalError}</div>}
        <form onSubmit={handleSubmit}>
          <div className="auth-field">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              required
              disabled={isLoading}
              maxLength={30}
            />
          </div>
          <div className="auth-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
              disabled={isLoading}
            />
          </div>
          <div className="auth-field">
            <label htmlFor="password1">Password</label>
            <input
              id="password1"
              type="password"
              value={formData.password1}
              onChange={(e) => handleInputChange('password1', e.target.value)}
              required
              disabled={isLoading}
              minLength={8}
            />
          </div>
          <div className="auth-field">
            <label htmlFor="password2">Confirm password</label>
            <input
              id="password2"
              type="password"
              value={formData.password2}
              onChange={(e) => handleInputChange('password2', e.target.value)}
              required
              disabled={isLoading}
              minLength={8}
            />
          </div>
          <div className="auth-field">
            <label>
              <input
                type="checkbox"
                checked={formData.terms_of_service}
                onChange={(e) => handleInputChange('terms_of_service', e.target.checked)}
              />
              {' '}I agree to the Terms of Service
            </label>
          </div>
          <div className="auth-field">
            <label>
              <input
                type="checkbox"
                checked={formData.privacy_policy}
                onChange={(e) => handleInputChange('privacy_policy', e.target.checked)}
              />
              {' '}I agree to the Privacy Policy
            </label>
          </div>
          <div className="auth-field">
            <label>
              <input
                type="checkbox"
                checked={formData.cookie_policy}
                onChange={(e) => handleInputChange('cookie_policy', e.target.checked)}
              />
              {' '}I agree to the Cookie Policy
            </label>
          </div>
          <div className="auth-actions">
            <button className="primary-button" type="submit" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create account'}
            </button>
            <Link to="/login">Already have an account? Log in</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Signup;
